// react
import { useEffect, useRef } from 'react';

// relative
import type { DateRange } from '../types';
import useLiveMode from './useLiveMode';

import { DateTime } from 'luxon';

const LIVEMODE_POLL_INTERVAL_MS = 10000; // 10 seconds

interface LiveModeManager {
  liveMode: boolean;
  toggleLiveMode: () => void;
}

/**
 * Custom hook to manage live mode state and interval polling.
 * Automatically sets up the interval when the page loads with liveMode=true in URL.
 *
 * Uses a ref to avoid stale closures in the setInterval callback — the callback
 * always reads the latest setDateRangeInUrl without needing it as an effect dep.
 */
function useLiveModeManager(
  dateRange: DateRange,
  setDateRangeInUrl: (range: DateRange) => void,
): LiveModeManager {
  const [liveMode, setLiveModeInUrl] = useLiveMode();
  const previousDateRangeRef = useRef<DateRange | null>(null);

  // Ref to always call the latest version of setDateRangeInUrl from the interval
  const setDateRangeRef = useRef(setDateRangeInUrl);
  useEffect(() => {
    setDateRangeRef.current = setDateRangeInUrl;
  });

  useEffect(() => {
    if (!liveMode) return;

    const updateDateRangeToLastHour = () => {
      const to = DateTime.now().plus({ minutes: 1 });
      const from = to.minus({ hours: 1 });
      setDateRangeRef.current({ from, to });
    };

    updateDateRangeToLastHour();
    const intervalId = setInterval(
      updateDateRangeToLastHour,
      LIVEMODE_POLL_INTERVAL_MS,
    );

    return () => clearInterval(intervalId);
  }, [liveMode]);

  const toggleLiveMode = () => {
    if (!liveMode) {
      previousDateRangeRef.current = dateRange;
      setLiveModeInUrl(true);
      return;
    }

    setLiveModeInUrl(false);

    if (previousDateRangeRef.current) {
      setDateRangeInUrl(previousDateRangeRef.current);
      previousDateRangeRef.current = null;
    }
  };

  return { liveMode, toggleLiveMode };
}

export default useLiveModeManager;
